<template>
  <b-container>
    <div v-if="job" class="view-job-page page">
      <login-register ref="login" v-if="!isAuthenticated"></login-register>
      <send-message-form ref="message" :job="job"></send-message-form>

      <div class="page__header">
        <b-breadcrumb :items="items"></b-breadcrumb>
      </div>
      <div
        class="view-job__content page__card page__card--full page__card--no-bg mt-0"
      >
        <div class="view-job__left">
          <h2 class="job-title">{{ job.name }}</h2>
          <div class="job-location d-flex">
            <b-img-lazy
              :src="require('@/assets/icons/location-b.svg')"
              v-bind="locationProps"
            ></b-img-lazy>
            <h5>{{ job.city }}, {{ job.state }}</h5>
          </div>
          <job-photo-list class="job-photos" />
          <div class="job-tabs">
            <b-navbar-nav v-model="tabIndex" class="job-tabs__nav flex-row">
              <b-nav-item
                v-for="tab in tabItems"
                :key="tab.index"
                :class="{
                  active: tabIndex == tab.index
                }"
                @click="tabIndex = tab.index"
                >{{ tab.label }}<span class="dots"></span
              ></b-nav-item>
            </b-navbar-nav>
          </div>

          <div v-if="tabIndex === 0" class="job-description">
            <div class="job-rate d-flex flex-row">
              <rate
                :value="reviewRating"
                :has-label="false"
                :disabled="true"
              ></rate>
              <p>({{ reviewTotal }} review{{ reviewTotal > 1 ? 's' : '' }})</p>
            </div>
            <p>{{ job.description }}</p>
          </div>
          <div v-else-if="tabIndex === 1" class="job-reviews">
            <review-list :job="job" />
          </div>
          <div v-else-if="tabIndex === 2" class="job-faq">
            <faq-list />
          </div>

          <div class="job-similar pt-5">
            <h2 class="page-title">Similar Products</h2>
            <similar-jobs-list />
          </div>
        </div>
        <div class="view-job__right">
          <div ref="sticky" class="view-job__right__border">
            <rent-form
              :job="job"
              @not-auth="onNotAuth"
              @has-date="refreshSticky"
            />
            <owner-details
              :job="job"
              @not-auth="onNotAuth"
              @send-message="$refs.message.show()"
            />
          </div>
        </div>
      </div>
    </div>
  </b-container>
</template>
<script>
/**
 * ==================================================================================
 * View Job/Tool page
 * ==================================================================================
 **/

import { mapState, mapMutations, mapActions } from 'vuex'
import Sticky from '@/utils/mixins/sticky'
import LoginRegister from '@/components/modals/LoginRegister'
import RentForm from '@/components/forms/RentForm'
import Rate from '@/components/inputs/Rate'
import OwnerDetails from '@/components/cards/OwnerDetails'
import JobPhotoList from '@/components/lists/JobPhotoList'
import SimilarJobsList from '@/components/lists/SimilarJobsList'
import ReviewList from '@/components/lists/ReviewList'
import FAQList from '@/components/lists/FAQList'
import SendMessageForm from '@/components/forms/SendMessageForm'

export default {
  metaInfo: function() {
    const job = this.job
    let description = ''

    if (job) {
      description =
        job.description.length > 160
          ? job.description.substring(0, 157) + '...'
          : job.description
    }

    return job
      ? {
          title: `${job.name}`,
          meta: [
            {
              property: 'og:title',
              content: `${job.name}`
            },
            {
              property: 'og:description',
              content: `${description}`
            }
          ]
        }
      : {}
  },
  components: {
    RentForm,
    Rate,
    OwnerDetails,
    JobPhotoList,
    SimilarJobsList,
    ReviewList,
    SendMessageForm,
    LoginRegister,
    'faq-list': FAQList
  },
  mixins: [Sticky],
  computed: {
    ...mapState({
      user: state => state.auth.user,
      isAuthenticated: state => state.auth.isAuthenticated,
      job: state => state.job.jobDetails
    }),
    items() {
      return [
        { text: 'Search', to: { name: 'search' } },
        { text: 'Tools', to: { name: 'search' } },
        { text: this.job.name, active: true }
      ]
    },

    review() {
      if (!this.job) return
      return this.job.reviews
    },

    reviewTotal() {
      return this.review ? this.review.total : 0
    },

    reviewRating() {
      return this.review ? this.review.average : 0
    }
  },
  data: function() {
    return {
      loading: false,
      tabIndex: 0,
      tabItems: [
        { index: 0, label: 'Description' },
        { index: 1, label: 'Reviews' },
        { index: 2, label: 'FAQ' }
      ],
      locationProps: {
        width: 23,
        height: 23
      }
    }
  },
  methods: {
    ...mapActions({
      getJobDetails: 'job/getJobDetails'
    }),
    ...mapMutations({
      clearJobDetails: 'job/clearJobDetails'
    }),

    async fetchJob() {
      if (this.loading) return
      this.loading = true
      await this.getJobDetails({
        id: this.$route.params.id,
        isView: true
      })
        .then(() => {
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          this.$router.replace({ name: 'page-not-found' })
        })
    },

    refreshSticky() {
      if (this.stickyEnable) {
        this.$nextTick(() => {
          this.handleScroll()
        })
      }
    },

    onNotAuth(fn) {
      this.$refs.login.show(fn)
    }
  },
  created() {
    this.clearJobDetails()
    this.fetchJob()
  }
}
</script>
<style scoped lang="scss">
.view-job-page {
  .page__header {
    padding: 25px 0;

    .breadcrumb {
      margin: 0;
      padding: 0;
    }
  }

  .view-job__content {
    display: flex;
    flex-direction: row;

    .view-job__left {
      width: 70%;
      margin-right: 7%;
    }

    .view-job__right {
      flex-grow: 1;

      &__border {
        > * {
          margin-bottom: 17.5px;
        }
      }

      .rent-form,
      .owner-details {
        max-width: 600px;
      }
    }

    .job-title {
      margin-top: 20px;
      margin-bottom: 12.5px;
    }

    .job-location {
      img {
        margin-right: 7.5px;
      }

      h5 {
        font-weight: 400;
        color: #6c6c6c;
      }
    }

    .job-photos {
    }

    .job-tabs {
      margin: 3rem;

      &__nav {
        .nav-item {
          padding-right: 40px;

          > a {
            font-size: 1.7rem;
            font-weight: 400;
            color: #b5b5b5;
          }

          .dots {
            display: block;
            width: 6px;
            height: 6px;
            background: transparent;
            border-radius: 50%;
            margin: 0 auto;
          }
        }

        .nav-item.active {
          > a {
            font-weight: 700;
            color: var(--font-color);
          }

          .dots {
            background: var(--font-color);
          }
        }

        .nav-item:last-child {
          margin-right: 0;
        }
      }
    }

    .job-description {
      > p {
        font-size: 1.1rem;
        line-height: 1.5rem;
      }
    }

    .job-rate {
      > p {
        font-size: 0.9rem;
        line-height: 1.1rem;
        color: #6c6c6c;
      }

      .rate {
        margin-right: 5px;
      }
    }

    .job-similar {
      margin-top: 100px;
      margin-top: 3rem;
      margin-bottom: 3rem;
    }
  }
}

@media (max-width: 768px) {
  .view-job-page {
    .view-job__content {
      flex-direction: column;

      .view-job__left,
      .view-job__right {
        width: 100%;
        margin-left: 0;
      }

      .view-job__right {
        .rent-form,
        .owner-details {
          max-width: inherit;
        }
      }

      .job-tabs {
        margin: 1rem;

        &__nav {
          .nav-item {
            padding-right: 20px;
          }
        }
      }

      .job-similar {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
    }
  }
}
</style>
